import {
  BorrowalVsNonBorrowalTableRow,
} from "../../../../api/tenantInternalReports";
import {
  DateWindow,
  DEFAULT_REPORT_AMOUNT_UNIT,
  toDisplayAmount,
} from "../../../../api/tenantInternalReports";
import {
  MergedHeadersSet,
  ReportTableColumn,
} from "../../../../components/ui/reportTable";
import { cn } from "../../../../lib/utils";
import { pickWindowData } from "../../logic/reportWindows";


const DIVIDER = "border-l border-l-[#E5E5E5]";
const CATEGORY_CELL =
  "h-[52px] px-2 py-2 text-sm text-left whitespace-nowrap text-[#0A0A0A] font-medium";
const DATA_CELL =
  "h-[52px] px-2 py-2 text-sm text-right whitespace-nowrap text-[#0A0A0A] font-normal";
const CATEGORY_HEADER =
  "h-14 px-2 align-center text-left text-sm font-semibold whitespace-nowrap text-[#374151] border-b border-[#E5E5E5]";
const DATA_HEADER =
  "h-14 px-2 align-center text-right text-sm font-semibold whitespace-nowrap text-[#374151] border-b border-[#E5E5E5]";
const TIER1_PILL_CELL = "p-1 align-bottom";
const TIER1_PILL =
  "flex h-14 items-center justify-center rounded-lg px-4 text-sm font-semibold bg-[#F5F5F4] text-[#374151]";

const tier1Pill = (label: string) => <div className={TIER1_PILL}>{label}</div>;
const TIER2 =
  "h-11 px-2 text-center text-sm font-semibold whitespace-nowrap text-[#263AAD]";

const AMOUNT_LABEL = `Total Amount Involved (${DEFAULT_REPORT_AMOUNT_UNIT.label})`;

const formatCount = (value: number | null) =>
  value == null ? "-" : value.toLocaleString("en-IN");

const formatAmount = (rawValue: number | null) => {
  const value = toDisplayAmount(rawValue);
  return value == null
    ? "-"
    : value.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
};

export const buildBorrowalVsNonBorrowalColumns = (
  monthLabel: string,
  currentWindow: DateWindow,
  cumulativeWindow: DateWindow,
): ReportTableColumn<BorrowalVsNonBorrowalTableRow>[] => [
  {
    headerLabel: "Amount Bucket",
    headerClassName: CATEGORY_HEADER,
    getValue: (row) => (row.amount_bucket_label),
    columnCellClassName: CATEGORY_CELL,
  },
  {
    headerLabel: monthLabel,
    headerClassName: DATA_HEADER,
    getValue: (row) => formatCount(pickWindowData(row.data_by_date_windows, currentWindow)?.num_cases_borrowal ?? null),
    columnCellClassName: DATA_CELL,
  },
  {
    headerLabel: "Cumulative",
    headerClassName: DATA_HEADER,
    getValue: (row) => formatCount(pickWindowData(row.data_by_date_windows, cumulativeWindow)?.num_cases_borrowal ?? null),
    columnCellClassName: DATA_CELL,
  },
  {
    headerLabel: monthLabel,
    headerClassName: DATA_HEADER,
    getValue: (row) => formatAmount(pickWindowData(row.data_by_date_windows, currentWindow)?.total_amount_borrowal ?? null),
    columnCellClassName: DATA_CELL,
  },
  {
    headerLabel: "Cumulative",
    headerClassName: DATA_HEADER,
    getValue: (row) => formatAmount(pickWindowData(row.data_by_date_windows, cumulativeWindow)?.total_amount_borrowal ?? null),
    columnCellClassName: DATA_CELL,
  },
  {
    headerLabel: monthLabel,
    headerClassName: cn(DATA_HEADER, DIVIDER),
    getValue: (row) => formatCount(pickWindowData(row.data_by_date_windows, currentWindow)?.num_cases_non_borrowal ?? null),
    columnCellClassName: cn(DATA_CELL, DIVIDER),
  },
  {
    headerLabel: "Cumulative",
    headerClassName: DATA_HEADER,
    getValue: (row) => formatCount(pickWindowData(row.data_by_date_windows, cumulativeWindow)?.num_cases_non_borrowal ?? null),
    columnCellClassName: DATA_CELL,
  },
  {
    headerLabel: monthLabel,
    headerClassName: DATA_HEADER,
    getValue: (row) => formatAmount(pickWindowData(row.data_by_date_windows, currentWindow)?.total_amount_non_borrowal ?? null),
    columnCellClassName: DATA_CELL,
  },
  {
    headerLabel: "Cumulative",
    headerClassName: DATA_HEADER,
    getValue: (row) => formatAmount(pickWindowData(row.data_by_date_windows, cumulativeWindow)?.total_amount_non_borrowal ?? null),
    columnCellClassName: DATA_CELL,
  }
];

export const borrowalVsNonBorrowalMergedHeadersSets: MergedHeadersSet[] = [
  {
    mergedColumns: [
      { label: tier1Pill("Borrowal Cases"), startColumnIndex: 1, endColumnIndex: 4, className: TIER1_PILL_CELL},
      {label: tier1Pill("Non-Borrowal Cases"), startColumnIndex: 5, endColumnIndex: 8, className: TIER1_PILL_CELL},
    ],
  },
  {
    mergedColumns: [
      { label: "Number of Cases", startColumnIndex: 1, endColumnIndex: 2, className: TIER2 },
      { label: AMOUNT_LABEL, startColumnIndex: 3, endColumnIndex: 4, className: TIER2 },
      { label: "Number of Cases", startColumnIndex: 5, endColumnIndex: 6, className: cn(TIER2, DIVIDER)},
      { label: AMOUNT_LABEL, startColumnIndex: 7, endColumnIndex: 8, className: TIER2 },
    ],
  },
];

export interface MetricRow {
  label: string;
  value: number | null;
}

const METRIC_HEADER_PILL =
  "h-14 rounded-lg px-4 align-middle text-sm font-semibold bg-[#F5F5F4] text-[#263AAD]";
const METRIC_CELL_LEFT =
  "h-[52px] px-2 py-2 text-sm text-left whitespace-nowrap text-[#0A0A0A] font-medium border-b border-[#E5E5E5]";
const METRIC_CELL_RIGHT =
  "h-[52px] px-2 py-2 text-sm text-right whitespace-nowrap text-[#0A0A0A] font-normal border-b border-[#E5E5E5]";

export const metricColumns: ReportTableColumn<MetricRow>[] = [
  {
    headerLabel: "Metric",
    headerClassName: `${METRIC_HEADER_PILL} text-left`,
    getValue: (metric) => metric.label,
    columnCellClassName: METRIC_CELL_LEFT,
  },
  {
    headerLabel: "Value",
    headerClassName: `${METRIC_HEADER_PILL} text-right`,
    getValue: (metric) =>
      metric.value == null
        ? "-"
        : metric.value.toLocaleString("en-IN", { maximumFractionDigits: 2 }),
    columnCellClassName: METRIC_CELL_RIGHT,
  },
];
